import React, { useState, useEffect } from 'react';
import { X, Building2, User, Mail, MapPin, FileText, Loader2, AlertCircle, CheckCircle, Copy, Check } from 'lucide-react';
import { createRetailerDirect, CreateRetailerData } from '../../services/retailerService';
import { createRetailerAccount } from '../../services/accountService';
import { auth } from '../../firebase';

interface AddRetailerModalProps {
  adminEmail: string;
  onClose: () => void;
  onSuccess: () => void;
}

const AddRetailerModal: React.FC<AddRetailerModalProps> = ({ adminEmail, onClose, onSuccess }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [credentials, setCredentials] = useState<{ email: string; password: string } | null>(null);
  const [copiedField, setCopiedField] = useState<'email' | 'password' | null>(null);

  const [formData, setFormData] = useState<CreateRetailerData>({
    businessName: '',
    contactName: '',
    email: '',
    phone: '',
    address: {
      street: '',
      city: '',
      province: 'ON',
      postalCode: '',
      country: 'Canada'
    },
    taxId: ''
  });

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isSubmitting) {
        if (credentials) {
          onSuccess();
        }
        onClose();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose, onSuccess, isSubmitting, credentials]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name.startsWith('address.')) {
      const addressField = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        address: { ...prev.address, [addressField]: value }
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      // Validate required fields
      if (!formData.businessName || !formData.contactName || !formData.email || !formData.phone || !formData.taxId) {
        throw new Error('Please fill in all required fields');
      }

      if (!formData.address.street || !formData.address.city || !formData.address.postalCode) {
        throw new Error('Please fill in the complete address');
      }

      // Verify admin is authenticated
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('Session expired. Please refresh the page and log in again.');
      }

      // Force token refresh to ensure auth is current
      await currentUser.getIdToken(true);

      // Create Firebase Auth account first
      const accountResult = await createRetailerAccount(
        formData.email,
        formData.businessName
      );

      // Create retailer record in Firestore
      await createRetailerDirect(formData, adminEmail);

      // Show credentials
      setCredentials({
        email: accountResult.email,
        password: accountResult.tempPassword
      });

    } catch (err: any) {
      console.error('Create account error:', err);
      // Handle Firebase function errors
      if (err.code === 'functions/unauthenticated') {
        setError('Authentication error. Please log out and log back in, then try again.');
      } else if (err.code === 'functions/permission-denied') {
        setError('Permission denied. Your admin account may not have the required permissions.');
      } else if (err.code === 'functions/already-exists') {
        setError('An account with this email address already exists.');
      } else {
        setError(err.message || 'Failed to create retailer account');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const copyToClipboard = async (text: string, field: 'email' | 'password') => {
    await navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleDone = () => {
    onSuccess();
    onClose();
  };

  const inputClass =
    'w-full px-4 py-3 bg-paper-bright border border-ink-200 text-sm text-ink-900 placeholder:text-ink-500 focus:border-ember focus:outline-none focus:ring-1 focus:ring-ember/20 transition-colors duration-150';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-forge-deep/70 backdrop-blur-sm" onClick={credentials ? handleDone : onClose}></div>

      <div className="relative bg-paper-bright shadow-float w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-forge text-paper px-6 py-5 relative">
          <button
            onClick={credentials ? handleDone : onClose}
            className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center text-paper/70 hover:text-paper hover:bg-paper/10 transition-colors duration-150"
            aria-label="Close"
          >
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-ember flex items-center justify-center flex-shrink-0">
              <Building2 className="w-5 h-5 text-paper-warm" strokeWidth={1.5} />
            </div>
            <div>
              <p className="font-mono text-[11px] tracking-micro uppercase text-ember-soft mb-1">— New retailer</p>
              <h2 className="font-editorial italic font-light text-2xl text-paper-warm tracking-tight">
                Add retailer.
              </h2>
              <p className="text-paper/70 text-xs mt-1">Manually create account for existing customer</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 lg:p-8 overflow-y-auto max-h-[calc(90vh-180px)]">
          {error && (
            <div className="mb-5 bg-ember-soft/25 border-l-2 border-ember p-3 flex items-start gap-2.5">
              <AlertCircle className="w-4 h-4 text-ember flex-shrink-0 mt-0.5" strokeWidth={1.5} />
              <p className="text-ember-deep text-sm">{error}</p>
            </div>
          )}

          {credentials ? (
            // Success - Show Credentials
            <div className="text-center">
              <div className="w-14 h-14 bg-olive/15 rounded-full flex items-center justify-center mx-auto mb-5">
                <CheckCircle className="w-6 h-6 text-olive" strokeWidth={1.5} />
              </div>
              <h3 className="font-editorial italic text-2xl text-ink-950 mb-2 tracking-tight">Account created.</h3>
              <p className="text-ink-700 text-sm mb-6">Share these login credentials with your customer:</p>

              <div className="bg-paper border border-ink-200 p-5 text-left space-y-4">
                <div>
                  <label className="block font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-2">Email</label>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 bg-paper-bright border border-ink-200 px-3 py-2 font-mono text-sm text-ink-900">
                      {credentials.email}
                    </code>
                    <button
                      onClick={() => copyToClipboard(credentials.email, 'email')}
                      className="p-2 hover:bg-paper-bright border border-ink-200 hover:border-ink-300 transition-colors duration-150"
                      title="Copy email"
                    >
                      {copiedField === 'email' ? (
                        <Check className="w-4 h-4 text-olive" strokeWidth={1.5} />
                      ) : (
                        <Copy className="w-4 h-4 text-ink-500" strokeWidth={1.5} />
                      )}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-2">Temporary password</label>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 bg-paper-bright border border-ink-200 px-3 py-2 font-mono text-sm text-ink-900">
                      {credentials.password}
                    </code>
                    <button
                      onClick={() => copyToClipboard(credentials.password, 'password')}
                      className="p-2 hover:bg-paper-bright border border-ink-200 hover:border-ink-300 transition-colors duration-150"
                      title="Copy password"
                    >
                      {copiedField === 'password' ? (
                        <Check className="w-4 h-4 text-olive" strokeWidth={1.5} />
                      ) : (
                        <Copy className="w-4 h-4 text-ink-500" strokeWidth={1.5} />
                      )}
                    </button>
                  </div>
                </div>
              </div>

              <p className="text-sm text-ink-500 mt-4">
                The customer should change their password after first login.
              </p>

              <button
                onClick={handleDone}
                className="mt-6 px-7 py-3 bg-ember hover:bg-ember-deep text-paper-warm text-sm font-medium transition-colors duration-150 ease-editorial"
              >
                Done
              </button>
            </div>
          ) : (
            // Form
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Business Information */}
              <div>
                <h3 className="font-editorial italic text-lg text-ink-950 tracking-tight flex items-center gap-2 mb-4">
                  <Building2 className="w-4 h-4 text-ember" strokeWidth={1.5} />
                  Business information
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="block font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-2">Business name *</label>
                    <input
                      type="text"
                      name="businessName"
                      value={formData.businessName}
                      onChange={handleInputChange}
                      className={inputClass}
                      placeholder="e.g., Green Valley Hardware"
                      required
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-2">HST / Business number *</label>
                    <input
                      type="text"
                      name="taxId"
                      value={formData.taxId}
                      onChange={handleInputChange}
                      className={`${inputClass} font-mono`}
                      placeholder="e.g., 123456789RT0001"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div>
                <h3 className="font-editorial italic text-lg text-ink-950 tracking-tight flex items-center gap-2 mb-4">
                  <User className="w-4 h-4 text-ember" strokeWidth={1.5} />
                  Contact information
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-2">Contact name *</label>
                    <input
                      type="text"
                      name="contactName"
                      value={formData.contactName}
                      onChange={handleInputChange}
                      className={inputClass}
                      placeholder="e.g., John Smith"
                      required
                    />
                  </div>
                  <div>
                    <label className="block font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-2">Phone *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className={inputClass}
                      placeholder="e.g., (416) 555-1234"
                      required
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-2">
                      <Mail className="w-3 h-3 inline mr-1" strokeWidth={1.5} />
                      Email address * (This will be their login)
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className={inputClass}
                      placeholder="e.g., john@greenvalleyhardware.com"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Address */}
              <div>
                <h3 className="font-editorial italic text-lg text-ink-950 tracking-tight flex items-center gap-2 mb-4">
                  <MapPin className="w-4 h-4 text-ember" strokeWidth={1.5} />
                  Business address
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="block font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-2">Street address *</label>
                    <input
                      type="text"
                      name="address.street"
                      value={formData.address.street}
                      onChange={handleInputChange}
                      className={inputClass}
                      placeholder="e.g., 123 Main Street"
                      required
                    />
                  </div>
                  <div>
                    <label className="block font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-2">City *</label>
                    <input
                      type="text"
                      name="address.city"
                      value={formData.address.city}
                      onChange={handleInputChange}
                      className={inputClass}
                      placeholder="e.g., Toronto"
                      required
                    />
                  </div>
                  <div>
                    <label className="block font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-2">Province *</label>
                    <select
                      name="address.province"
                      value={formData.address.province}
                      onChange={handleInputChange}
                      className={`${inputClass} bg-paper-bright`}
                      required
                    >
                      <option value="ON">Ontario</option>
                      <option value="QC">Quebec</option>
                      <option value="BC">British Columbia</option>
                      <option value="AB">Alberta</option>
                      <option value="MB">Manitoba</option>
                      <option value="SK">Saskatchewan</option>
                      <option value="NS">Nova Scotia</option>
                      <option value="NB">New Brunswick</option>
                      <option value="NL">Newfoundland and Labrador</option>
                      <option value="PE">Prince Edward Island</option>
                      <option value="NT">Northwest Territories</option>
                      <option value="YT">Yukon</option>
                      <option value="NU">Nunavut</option>
                    </select>
                  </div>
                  <div>
                    <label className="block font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-2">Postal code *</label>
                    <input
                      type="text"
                      name="address.postalCode"
                      value={formData.address.postalCode}
                      onChange={handleInputChange}
                      className={`${inputClass} uppercase`}
                      placeholder="e.g., M5V 1A1"
                      required
                    />
                  </div>
                  <div>
                    <label className="block font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-2">Country</label>
                    <input
                      type="text"
                      value="Canada"
                      disabled
                      className="w-full px-4 py-3 border border-ink-200 bg-paper text-ink-500 text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t border-ink-200">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-4 py-2.5 border border-ink-200 hover:border-ink-300 hover:bg-paper text-ink-700 text-sm font-medium transition-colors duration-150"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 px-4 py-2.5 bg-ember hover:bg-ember-deep disabled:opacity-50 text-paper-warm text-sm font-medium transition-colors duration-150 ease-editorial flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
                      Creating account...
                    </>
                  ) : (
                    <>
                      <FileText className="w-4 h-4" strokeWidth={1.5} />
                      Create account
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddRetailerModal;
